import React, { useState, useEffect } from 'react';
import useLoyaltyStore from './useLoyaltyStore';
import AnimatedModal from '../shared/AnimatedModal';
import ImageDropzone from '../shared/ImageDropzone';

const RewardFormModal = ({ initialCategories = [], initialPartners = [] }) => {
  const { closeModal, modalMode, editingItem, saveReward, partners, categories } = useLoyaltyStore();
  const isEdit = modalMode === 'edit';

  const partnerOptions = partners.length > 0 ? partners : initialPartners;
  const categoryOptions = categories.length > 0 ? categories : initialCategories;

  const [form, setForm] = useState({
    name: '', description: '', loyalty_category_id: '', loyalty_partner_id: '',
    limitless_quota: true, max_vouchers: '', points_required: '0',
    terms_and_conditions_url: '', terms_and_conditions_text: '', status: 'active',
    claimable: true
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit && editingItem) {
      setForm({
        name: editingItem.name || '',
        description: editingItem.description || '',
        loyalty_category_id: editingItem.category?.id || '',
        loyalty_partner_id: editingItem.partner?.id || '',
        limitless_quota: editingItem.limitless_quota !== false,
        max_vouchers: editingItem.max_vouchers || '',
        points_required: editingItem.points_required ?? '0',
        terms_and_conditions_url: editingItem.terms_and_conditions_url || '',
        terms_and_conditions_text: editingItem.terms_and_conditions_text || '',
        status: editingItem.status || 'active',
        claimable: editingItem.claimable !== false
      });
      if (editingItem.image_url) setImagePreview(editingItem.image_url);
    }
  }, [isEdit, editingItem]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleFileSelect = (file) => {
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Reward name is required';
    if (!form.description.trim()) errs.description = 'Description is required';
    if (!form.loyalty_category_id) errs.loyalty_category_id = 'Category is required';
    if (!form.loyalty_partner_id) errs.loyalty_partner_id = 'Partner is required';
    if (!form.limitless_quota && (!form.max_vouchers || Number(form.max_vouchers) <= 0)) errs.max_vouchers = 'Max vouchers required';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSaving(true);
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const fd = new FormData();
    fd.append('authenticity_token', csrfToken);
    Object.entries(form).forEach(([k, v]) => fd.append(`reward[${k}]`, v));
    if (imageFile) fd.append('reward[image]', imageFile);

    try { await saveReward(fd); } finally { setSaving(false); }
  };

  const footer = (handleClose) => (
    <>
      <button type="button" className="btn btn-outline-secondary" onClick={handleClose}>Cancel</button>
      <button type="button" className="btn btn-success" disabled={saving} onClick={handleSubmit}>
        {saving ? 'Saving...' : 'Save Reward'}
      </button>
    </>
  );

  return (
    <AnimatedModal
      title={isEdit ? 'Edit Reward' : 'Add New Reward'}
      onClose={closeModal}
      size="modal-lg"
      footer={footer}
    >
      <div className="form-group">
        <label>Reward Image</label>
        <ImageDropzone preview={imagePreview} onFileSelect={handleFileSelect} hint="Ideal size: 800x400px" />
      </div>

      <div className="form-group">
        <label>Reward Name <span className="text-danger">*</span></label>
        <input type="text" className={`form-control${errors.name ? ' is-invalid' : ''}`} name="name" value={form.name} onChange={handleChange} placeholder="Enter reward name" />
        <i className="form-group__bar" />
        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
      </div>

      <div className="form-group">
        <label>Description <span className="text-danger">*</span></label>
        <textarea className={`form-control${errors.description ? ' is-invalid' : ''}`} name="description" value={form.description} onChange={handleChange} rows={3} placeholder="Enter reward description" />
        <i className="form-group__bar" />
        {errors.description && <div className="invalid-feedback">{errors.description}</div>}
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="form-group">
            <label>Category <span className="text-danger">*</span></label>
            <select className={`form-control${errors.loyalty_category_id ? ' is-invalid' : ''}`} name="loyalty_category_id" value={form.loyalty_category_id} onChange={handleChange}>
              <option value="">Select category...</option>
              {categoryOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <i className="form-group__bar" />
            {errors.loyalty_category_id && <div className="invalid-feedback">{errors.loyalty_category_id}</div>}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group">
            <label>Partner <span className="text-danger">*</span></label>
            <select className={`form-control${errors.loyalty_partner_id ? ' is-invalid' : ''}`} name="loyalty_partner_id" value={form.loyalty_partner_id} onChange={handleChange}>
              <option value="">Select partner...</option>
              {partnerOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <i className="form-group__bar" />
            {errors.loyalty_partner_id && <div className="invalid-feedback">{errors.loyalty_partner_id}</div>}
          </div>
        </div>
      </div>

      <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
        <label htmlFor="reward-claimable" className="mb-0" style={{ cursor: 'pointer' }}>
          <div>Claimable</div>
          <small className="text-muted">Allow users to claim this reward</small>
        </label>
        <div className="toggle-switch" style={{ marginBottom: 0 }}>
          <input type="checkbox" className="toggle-switch__checkbox" id="reward-claimable" name="claimable" checked={form.claimable} onChange={handleChange} />
          <i className="toggle-switch__helper" />
        </div>
      </div>

      <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
        <div>
          <div>Limitless Quota</div>
          <small className="text-muted">Turn off to set a maximum number of vouchers</small>
        </div>
        <div className="toggle-switch" style={{ marginBottom: 0 }}>
          <input type="checkbox" className="toggle-switch__checkbox" id="limitless-quota" name="limitless_quota" checked={form.limitless_quota} onChange={handleChange} />
          <i className="toggle-switch__helper" />
        </div>
      </div>

      {!form.limitless_quota && (
        <div className="form-group">
          <label>Max Vouchers <span className="text-danger">*</span></label>
          <input type="number" className={`form-control${errors.max_vouchers ? ' is-invalid' : ''}`} name="max_vouchers" value={form.max_vouchers} onChange={handleChange} min="1" />
          <i className="form-group__bar" />
          {errors.max_vouchers && <div className="invalid-feedback">{errors.max_vouchers}</div>}
        </div>
      )}

      <div className="form-group">
        <label>Points Required</label>
        <input type="number" className="form-control" name="points_required" value={form.points_required} onChange={handleChange} min="0" />
        <i className="form-group__bar" />
        <small className="text-muted">Set to 0 for Hermina Run items</small>
      </div>

      <div className="form-group">
        <label>Terms & Conditions URL</label>
        <input type="text" className="form-control" name="terms_and_conditions_url" value={form.terms_and_conditions_url} onChange={handleChange} placeholder="https://..." />
        <i className="form-group__bar" />
      </div>

      <div className="form-group">
        <label>Terms & Conditions Text</label>
        <textarea className="form-control" name="terms_and_conditions_text" value={form.terms_and_conditions_text} onChange={handleChange} rows={3} placeholder="Enter terms and conditions..." />
        <i className="form-group__bar" />
      </div>
    </AnimatedModal>
  );
};

export default RewardFormModal;
